import { useMemo, useState } from "react";
import type { KeyboardEvent } from "react";

import type { Pro014StepFourData, Pro014StepFourItem } from "@/@types/campaign";
import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import type { IGradeTarget } from "@/@types/campaign-grade";
import { toast } from "@/components/ui/use-toast";
import { DEFAULT_PAGE } from "@/constants/pagination";

import { findItemsWithoutGrade } from "../../utils/grade-targets";
import { TOAST_VARIANT } from "../../utils/messages";
import { useExcelImport } from "../components/shared/use-excel-import";
import {
  CONFLICT_FILTER,
  PRO014_MIN_ACTIVE_ITEMS,
  PRO014_MIN_ITEMS,
  PRO014_PRODUCTS_PAGE_SIZE,
  PRO014_PRODUCTS_TEXTS,
  SKU_TEMPLATE_FILENAME,
  SKU_TEMPLATE_PATH,
} from "../constants";
import type { ConflictFilter, Pro014ProductsStep, ProductsStepErrors } from "../types";

const ENTER_KEY = "Enter";

interface UsePro014ProductsStepParams {
  data: Pro014StepFourData;
  updateData: (data: Partial<Pro014StepFourData>) => void;
  nextStep: () => void;
  conflicts: CampaignConflictItem[];
  conflictFilter: ConflictFilter;
  onConflictFilterChange?: (filter: ConflictFilter) => void;
  leavesTooFewActive?: boolean;
}

const MATCHES_CONFLICT_FILTER: Record<ConflictFilter, (hasConflict: boolean) => boolean> = {
  [CONFLICT_FILTER.ALL]: () => true,
  [CONFLICT_FILTER.CONFLICT]: (hasConflict: boolean) => hasConflict,
  [CONFLICT_FILTER.CLEAN]: (hasConflict: boolean) => !hasConflict,
};

const matchesSearch = (item: Pro014StepFourItem, term: string): boolean =>
  item.sku.toLowerCase().includes(term) || (item.name ?? "").toLowerCase().includes(term);

const downloadStaticFile = (path: string, filename: string): void => {
  const link: HTMLAnchorElement = document.createElement("a");
  link.href = path;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function usePro014ProductsStep({
  data,
  updateData,
  nextStep,
  conflicts,
  conflictFilter,
  onConflictFilterChange,
  leavesTooFewActive = false,
}: UsePro014ProductsStepParams): Pro014ProductsStep {
  const [itemCode, setItemCode] = useState<string>("");
  const [gradeItem, setGradeItem] = useState<Pro014StepFourItem | null>(null);
  const [errors, setErrors] = useState<ProductsStepErrors>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [requestedPage, setRequestedPage] = useState<number>(DEFAULT_PAGE);

  const conflictByCode: Map<string, CampaignConflictItem> = useMemo(
    () => new Map(conflicts.map((conflict: CampaignConflictItem) => [conflict.itemCode, conflict])),
    [conflicts],
  );

  const visibleItems: Pro014StepFourItem[] = useMemo(() => {
    const term: string = searchTerm.trim().toLowerCase();
    const matchesFilter: (hasConflict: boolean) => boolean =
      MATCHES_CONFLICT_FILTER[conflictFilter] ?? MATCHES_CONFLICT_FILTER[CONFLICT_FILTER.ALL];
    return data.items
      .filter((item: Pro014StepFourItem) => matchesFilter(conflictByCode.has(item.sku)))
      .filter((item: Pro014StepFourItem) => !term || matchesSearch(item, term));
  }, [data.items, conflictFilter, conflictByCode, searchTerm]);

  const totalPages: number = Math.max(
    DEFAULT_PAGE,
    Math.ceil(visibleItems.length / PRO014_PRODUCTS_PAGE_SIZE),
  );
  const currentPage: number = Math.min(requestedPage, totalPages);
  const pageOffset: number = (currentPage - 1) * PRO014_PRODUCTS_PAGE_SIZE;
  const pageItems: Pro014StepFourItem[] = visibleItems.slice(
    pageOffset,
    pageOffset + PRO014_PRODUCTS_PAGE_SIZE,
  );

  const itemsWithoutGrade: string[] = data.gradeRestricted
    ? findItemsWithoutGrade(data.items)
    : [];
  const canContinue: boolean =
    data.items.length >= PRO014_MIN_ITEMS &&
    itemsWithoutGrade.length === 0 &&
    !leavesTooFewActive;
  const activeFloorError: string | undefined = leavesTooFewActive
    ? PRO014_PRODUCTS_TEXTS.MIN_ACTIVE_ITEMS_ERROR(PRO014_MIN_ACTIVE_ITEMS)
    : undefined;

  const excel: ReturnType<typeof useExcelImport> = useExcelImport(data.items);

  const changeItemCode = (value: string): void => {
    setItemCode(value);
    setErrors((previous: ProductsStepErrors) => ({ ...previous, itemCode: undefined }));
  };

  const addItem = (): void => {
    const code: string = itemCode.trim();
    if (!code) {
      setErrors({ ...errors, itemCode: PRO014_PRODUCTS_TEXTS.EMPTY_SKU_ERROR });
      return;
    }
    if (data.items.some((item: Pro014StepFourItem) => item.sku === code)) {
      setErrors({ ...errors, itemCode: PRO014_PRODUCTS_TEXTS.DUPLICATE_SKU_ERROR });
      return;
    }
    updateData({ items: [...data.items, { sku: code }] });
    setItemCode("");
    setErrors({});
  };

  const handleItemCodeKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== ENTER_KEY) return;
    event.preventDefault();
    addItem();
  };

  const removeItem = (sku: string): void => {
    updateData({ items: data.items.filter((item: Pro014StepFourItem) => item.sku !== sku) });
    setRequestedPage(DEFAULT_PAGE);
    setErrors({});
  };

  const removeConflictingItems = (): void => {
    updateData({
      items: data.items.filter((item: Pro014StepFourItem) => !conflictByCode.has(item.sku)),
    });
    setRequestedPage(DEFAULT_PAGE);
    setErrors({});
  };

  const changeSearch = (value: string): void => {
    setSearchTerm(value);
    setRequestedPage(DEFAULT_PAGE);
  };

  const changeConflictFilter = (value: ConflictFilter): void => {
    onConflictFilterChange?.(value);
    setRequestedPage(DEFAULT_PAGE);
  };

  const confirmGrades = (grades: IGradeTarget[]): void => {
    if (!gradeItem) return;
    const nextGrades: IGradeTarget[] | undefined = grades.length > 0 ? grades : undefined;
    updateData({
      items: data.items.map((item: Pro014StepFourItem) =>
        item.sku === gradeItem.sku ? { ...item, grades: nextGrades } : item,
      ),
    });
    setGradeItem(null);
  };

  const continueToReview = (): void => {
    if (data.items.length < PRO014_MIN_ITEMS) {
      setErrors({ items: PRO014_PRODUCTS_TEXTS.MIN_ITEMS_ERROR(PRO014_MIN_ITEMS) });
      return;
    }
    if (itemsWithoutGrade.length > 0 || leavesTooFewActive) return;
    setErrors({});
    nextStep();
  };

  const downloadTemplate = (): void => {
    downloadStaticFile(SKU_TEMPLATE_PATH, SKU_TEMPLATE_FILENAME);
    toast({
      title: PRO014_PRODUCTS_TEXTS.TEMPLATE_DOWNLOADED_TITLE,
      description: PRO014_PRODUCTS_TEXTS.TEMPLATE_DOWNLOADED,
      variant: TOAST_VARIANT.DEFAULT,
    });
  };

  const confirmImport = async (items: Array<{ sku: string; name?: string }>): Promise<void> => {
    await excel.confirmImport(items, async (confirmed) => {
      updateData({ items: [...data.items, ...confirmed] });
      toast({
        title: PRO014_PRODUCTS_TEXTS.IMPORT_DONE_TITLE,
        description: PRO014_PRODUCTS_TEXTS.IMPORT_DONE(confirmed.length),
        variant: TOAST_VARIANT.DEFAULT,
      });
    });
  };

  return {
    itemCode,
    errors: { ...errors, items: errors.items ?? activeFloorError },
    searchTerm,
    gradeItem,
    conflictByCode,
    visibleCount: visibleItems.length,
    pageItems,
    pageOffset,
    currentPage,
    totalPages,
    itemsWithoutGrade,
    canContinue,
    fileInputRef: excel.fileInputRef,
    uploadProgress: excel.uploadProgress,
    isProcessing: excel.isProcessing,
    preview: excel.preview,
    changeItemCode,
    handleItemCodeKey,
    addItem,
    removeItem,
    removeConflictingItems,
    changeSearch,
    changeConflictFilter,
    goToPage: setRequestedPage,
    toggleRestriction: (enabled: boolean) => updateData({ gradeRestricted: enabled }),
    openGrade: setGradeItem,
    closeGrade: () => setGradeItem(null),
    confirmGrades,
    continueToReview,
    downloadTemplate,
    handleFileUpload: excel.handleFileUpload,
    closePreview: excel.closePreview,
    confirmImport,
  };
}
